import { AlertTriangle, Clock, Zap, CheckCircle, Gift, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TrialBannerProps {
  plan: string;
  status: string;
  daysRemaining: number | null;
  isTrialExpired: boolean;
  splitsThisMonth: number;
  splitLimit: number | null;
  onUpgradeClick?: () => void;
  onExtendTrialClick?: () => void;
  isProfessional?: boolean;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function TrialBanner({
  plan,
  status,
  daysRemaining,
  isTrialExpired,
  splitsThisMonth,
  splitLimit,
  onUpgradeClick,
  onExtendTrialClick,
  isProfessional = false,
}: TrialBannerProps) {
  // Determine if this is a professional plan
  const isPro = isProfessional || plan?.startsWith('professional');
  
  // Active paid plans don't need a banner (unless near limit)
  if (status === 'active' && plan !== 'trial' && !plan?.includes('trial')) {
    if (splitLimit !== null && splitsThisMonth >= splitLimit * 0.8) {
      return (
        <div className={`${isPro ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-yellow-500/10 border-yellow-500/20'} border rounded-lg p-4 mb-6`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${isPro ? 'bg-cyan-500/20' : 'bg-yellow-500/20'} rounded-lg`}>
                <AlertTriangle className={`h-5 w-5 ${isPro ? 'text-cyan-600' : 'text-yellow-600'}`} />
              </div>
              <div>
                <p className={`font-medium ${isPro ? 'text-cyan-700' : 'text-yellow-700'}`}>Approaching Payment Limit</p>
                <p className={`text-sm ${isPro ? 'text-cyan-600' : 'text-yellow-600'}`}>
                  You've used {splitsThisMonth} of {splitLimit} payments this month.
                </p>
              </div>
            </div>
            <Button size="sm" className={isPro ? 'bg-cyan-600 hover:bg-cyan-700' : 'bg-yellow-600 hover:bg-yellow-700'} onClick={onUpgradeClick}>
              Upgrade Plan
            </Button>
          </div>
        </div>
      );
    }
    return null;
  }

  // Trial expired
  if (isTrialExpired || status === 'expired') {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-destructive/20 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="font-medium text-destructive">
                {isPro ? 'Free Period Ended' : 'Trial Expired'}
              </p>
              <p className="text-sm text-destructive/80">
                Your {isPro ? '3-month free period' : 'free trial'} has ended. Upgrade or request an extension to continue.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {onExtendTrialClick && (
              <Button size="sm" variant="outline" onClick={onExtendTrialClick}>
                <Gift className="h-4 w-4 mr-2" />
                Extend
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={onUpgradeClick}>
              Choose a Plan
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Active trial with days remaining
  if (status === 'trialing' && daysRemaining !== null) {
    // For professionals, use teal/cyan theme
    if (isPro) {
      const urgency = daysRemaining <= 7 ? 'urgent' : daysRemaining <= 30 ? 'warning' : 'info';
      
      const colors = {
        urgent: {
          bg: 'bg-orange-500/10',
          border: 'border-orange-500/20',
          iconBg: 'bg-orange-500/20',
          icon: 'text-orange-600',
          text: 'text-orange-700',
          subtext: 'text-orange-600',
        },
        warning: {
          bg: 'bg-cyan-500/10',
          border: 'border-cyan-500/20',
          iconBg: 'bg-cyan-500/20',
          icon: 'text-cyan-600',
          text: 'text-cyan-700',
          subtext: 'text-cyan-600',
        },
        info: {
          bg: 'bg-teal-500/10',
          border: 'border-teal-500/20',
          iconBg: 'bg-teal-500/20',
          icon: 'text-teal-600',
          text: 'text-teal-700',
          subtext: 'text-teal-600',
        },
      };

      const c = colors[urgency];
      const monthsRemaining = Math.ceil(daysRemaining / 30);

      return (
        <div className={`${c.bg} border ${c.border} rounded-lg p-4 mb-6`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 ${c.iconBg} rounded-lg`}>
                <Sparkles className={`h-5 w-5 ${c.icon}`} />
              </div>
              <div>
                <p className={`font-medium ${c.text}`}>
                  {daysRemaining > 60 
                    ? `${monthsRemaining} months free remaining` 
                    : daysRemaining > 30 
                      ? `${monthsRemaining} months left in free period`
                      : daysRemaining === 1 
                        ? '1 day left in free period' 
                        : `${daysRemaining} days left in free period`}
                </p>
                <p className={`text-sm ${c.subtext}`}>
                  {splitLimit !== null 
                    ? `${splitsThisMonth} payments processed • ` 
                    : ''}
                  Unlimited payments during your 3-month free period
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="border-cyan-500/30 text-cyan-700 hover:bg-cyan-50" onClick={onUpgradeClick}>
                <Zap className="h-4 w-4 mr-2" />
                View Plans
              </Button>
            </div>
          </div>
        </div>
      );
    }
    
    // For merchants, use existing theme
    const urgency = daysRemaining <= 3 ? 'urgent' : daysRemaining <= 7 ? 'warning' : 'info';
    
    const colors = {
      urgent: {
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/20',
        iconBg: 'bg-orange-500/20',
        icon: 'text-orange-600',
        text: 'text-orange-700',
        subtext: 'text-orange-600',
      },
      warning: {
        bg: 'bg-yellow-500/10',
        border: 'border-yellow-500/20',
        iconBg: 'bg-yellow-500/20',
        icon: 'text-yellow-600',
        text: 'text-yellow-700',
        subtext: 'text-yellow-600',
      },
      info: {
        bg: 'bg-primary/10',
        border: 'border-primary/20',
        iconBg: 'bg-primary/20',
        icon: 'text-primary',
        text: 'text-primary',
        subtext: 'text-primary/80',
      },
    };

    const c = colors[urgency];

    return (
      <div className={`${c.bg} border ${c.border} rounded-lg p-4 mb-6`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 ${c.iconBg} rounded-lg`}>
              <Clock className={`h-5 w-5 ${c.icon}`} />
            </div>
            <div>
              <p className={`font-medium ${c.text}`}>
                {daysRemaining === 1 ? '1 day' : `${daysRemaining} days`} left in your trial
              </p>
              <p className={`text-sm ${c.subtext}`}>
                {splitLimit !== null 
                  ? `${splitsThisMonth}/${splitLimit} splits used • ` 
                  : ''}
                {daysRemaining <= 7 && onExtendTrialClick 
                  ? 'Need more time? Request an extension!' 
                  : 'Upgrade anytime to unlock more features'}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {daysRemaining <= 7 && onExtendTrialClick && (
              <Button size="sm" variant="outline" onClick={onExtendTrialClick}>
                <Gift className="h-4 w-4 mr-2" />
                Extend
              </Button>
            )}
            <Button size="sm" onClick={onUpgradeClick}>
              <Zap className="h-4 w-4 mr-2" />
              View Plans
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Pending activation (signed up but not activated yet)
  if (status === 'pending') {
    return (
      <div className={`${isPro ? 'bg-cyan-500/10 border-cyan-500/20' : 'bg-blue-500/10 border-blue-500/20'} border rounded-lg p-4 mb-6`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 ${isPro ? 'bg-cyan-500/20' : 'bg-blue-500/20'} rounded-lg`}>
            <CheckCircle className={`h-5 w-5 ${isPro ? 'text-cyan-600' : 'text-blue-600'}`} />
          </div>
          <div>
            <p className={`font-medium ${isPro ? 'text-cyan-700' : 'text-blue-700'}`}>Account Pending Activation</p>
            <p className={`text-sm ${isPro ? 'text-cyan-600' : 'text-blue-600'}`}>
              Your account is being set up. Contact support if this takes too long.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};